/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import React, {ReactElement, ReactNode} from 'react';
import isFunction from 'lodash.isfunction';

/**
 * The `Conditional` enum defines each type of conditional logic supported by
 * the `Render` component.
 */
export enum Conditional {
  If = 'If',
  ElseIf = 'ElseIf',
  Else = 'Else',
}

/**
 * Defines the `Render` Component props that are accepted from clients. All
 * props are required with the exception of the optional `strict` prop.
 */
export type RenderProps = {
  condition: Function | boolean | unknown;
  children: ReactNode;
  strict?: boolean;
};

/**
 * The `Render` component standardizes how components are conditionally rendered.
 * This ensures consistency, simplicity, and uniformity by providing a Component
 * dedicated to conditional rendering, thus we avoid having different approaches
 * for conditional rendering of components scattered throughout the Monorepo,
 * such as using ternary operators, using logical AND (&&) operators, (which
 * can result in the evaluated value being rendered, etc).
 *
 * ## Rendering ##
 *
 * In the most basic use-case, the `Render` component simply wraps a `Render.If`
 * Component and renders the `children` of the `Render.If` component when the
 * `condition` prop evaluates to `true`:
 *
 * ```typescript
 *   import Render from '@/dls/src/render/Render';
 *   ...
 *
 *   <Render>
 *     <Render.If condition={truthy | falsy}>
 *       <SomeComponent />
 *     </Render.If>
 *   </Render>
 *
 * ```
 *
 * In the above example, `<SomeComponent/>` will only be rendered when the given
 * condition is `true`, otherwise, it will be omitted.
 *
 * When you only need to have a single condition, the `<Render.If/>` component
 * can be used directly; i.e. no need to wrap it in a parent `Render` instance:
 *
 * ```typescript
 *   <Render.If condition={truthy | falsy}>
 *     <SomeComponent />
 *   </Render.If>
 * ```
 *
 * The `Render` component also accepts an the optional `strict` prop, which, when
 * set to `true`, will only render when the condition value or the return of the
 * condition function explicitly evaluates to `true` (i.e. strict equality checks).
 * By default, `strict` is set to `false`, meaning, the condition will be evaluated
 * using truthy assertions.
 *
 * *Strict mode is described in detail in the "Strict Mode Rendering" section below.*
 *
 * When the `condition` prop evaluates to `true` (or a truthy value), the provided
 * component(s) (children) will be rendered.
 *
 * ## Render Bypassing ##
 *
 * When the `condition` prop evaluates to `false` (or a falsy value), the provided
 * component(s) (`children`) will not be rendered:
 *
 * ```typescript
 *   const isEnabled = false;
 *   ...
 *   <Render>
 *     <Render.If condition={isEnabled}>
 *       <SomeComponent />
 *     </Render>
 *   </Render>
 *
 *   // or ...
 *   const someCondition = '';
 *   ...
 *
 *   <Render>
 *     <Render.If condition={isEnabled}>
 *       <SomeComponent />
 *     </Render>
 *   </Render>
 * ```
 *
 * ## Strict Mode Rendering ##
 *
 * Strict mode can be used via the (optional) `strict` prop in order to only
 * render when the condition value or condition function explicitly evaluates
 * to `true`; that is, truthy values aside from explicity boolean `true` will
 * not result in the provided components being rendered. By default, strict
 * mode is set to `false`:
 *
 * ```typescript
 *   <!-- components will render since condition is explicity true -->
 *   const isEnabled = true;
 *   ...
 *   <Render>
 *     <Render.If condition={isEnabled} strict>
 *       ...
 *     </Render.If>
 *   </Render>
 *   <Render condition={() => true}>
 *    ...
 *   </Render>
 *
 *
 *   <!-- components will not render since condition is not explicity true -->
 *   const isEnabled = 'true';
 *   ...
 *   <Render.If condition={isEnabled} strict>
 *    ...
 *   </Render.If>
 *   ...
 *
 *   <Render.If condition={() => 'true'}>
 *    ...
 *   </Render.If>
 * ```
 *
 * ## Render.ElseIf ##
 *
 * The `<Render.ElseIf>` component allows for rendering a different set of
 * components when the preceding `<Render.If>` condition evaluates to `false`.
 *
 * ```typescript
 *   const isAuthorized = true;
 *   const isEnabled    = false;
 *   ...
 *
 *   <Render>
 *     <Render.If condition={isEnabled}>
 *       <SomeComponent />
 *     </Render.If>
 *     <Render.ElseIf condition={isEnabled}>
 *       <SomeOtherComponent />
 *     </Render.ElseIf>
 *   </Render>
 * ```
 *
 * ## Render.Else ##
 *
 * The `<Render.Else>` component allows for rendering a set of components
 * when the conditions of the preceding `<Render.If>` and `<Render.ElseIf>`
 * are `false`.
 *
 * ```typescript
 *   const isEnabled = false;
 *   ...
 *
 *   <Render>
 *     <Render.If condition={isEnabled}>
 *       <SomeComponent />
 *     </Render.If>
 *     <Render.Else>
 *       <SomeOtherComponent />
 *     </Render.Else>
 *   </Render>
 * ```
 *
 * ## Nested Renders ##
 *
 * Just as is the case with imperative conditional statements, `<Render>`
 * instances can be nested *n-levels*:
 *
 * ```typescript
 *   const enableA = true;
 *   const enableB = true;
 *   const enableC = true;
 *   ...
 *
 *   <Render>
 *     <Render.If condition={enableA}>
 *       <Render.If condition={enableB}>
 *         <Render.If condition={enableC}>
 *          <SomeComponent />
 *         </Render.If>
 *         <Render.Else>
 *           <SomeOtherComponent />
 *         </Render.Else>
 *       </Render.If>
 *     </Render.If>
 *   </Render>
 * ```
 *
 * Likewise, any `children` can be nested within `<Render` component instances,
 * not just additional conditions:
 *
 * ```typescript
 *   const enableA = true;
 *   const enableB = true;
 *   const enableC = true;
 *   const enableD = true;
 *   ...
 *
 *   <Render>
 *     <Render.If condition={enableA}>
 *       <ComponentA />
 *       <Render.If condition={enableB}>
 *         <ComponentB />
 *         <Render.If condition={enableC}>
 *           <ComponentC />
 *           <Render.If condition={enableD}>
 *             <ComponentD />
 *           </Render.If>
 *         </Render.If>
 *       </Render.If>
 *     </Render.If>
 *   </Render>
 * ```
 *
 * *Note: while `Render` instances can be nested n-levels, it is important to keep
 * in mind that the same best practices surrounding imperative conditions still
 * apply, thus, nested `Render` instances should be used sparingly.*
 *
 * @param   condition The condition against which to test, this can be any value
 *          or a function to be executed to determine if the provided component
 *          should be rendered based on the functions return value.
 * @param   children The Component which is to be rendered if the given condition
 *          evaluates to a truthy value.
 * @param   strict When set to `true`, the component will only render if the provided
 *          condition explicitly evaluates to `true`; that is, a non boolean truthy
 *          value will not result in the component being rendered. Defaults to `false`.
 * @returns The provided children if the given condition evaluates to `true`, otherwise
 *          returns `undefined`.
 */
const Render = ({children}: {children: ReactNode}): ReactElement | undefined => {
  const conditions = getComponentTypes(children);
  const validate = (components: ReactElement[]) => {
    for (const component of components) {
      const {props} = cast(component);
      if (isTrue(props.condition, props.strict)) {
        return component;
      }
    }
  };
  return cast(
    validate(conditions.If) ||
    validate(conditions.ElseIf) ||
    conditions.Else
  );
};

/**
 * The `<Render.If/ElseIf>` components allows for providing the conditions for
 * which their respective `children` will be and rendered. Just as is the case
 * in traditional conditionals, the `<Render.If>` condition takes precedence
 * over the `<Render.ElseIf>` condition.
 */
Render.If = ({condition, children, strict = false}: RenderProps): ReactElement | undefined => {
  const isValid = isTrue(condition, strict);
  return isValid ? cast(children) : undefined;
};

/**
 * The `<Render.If/ElseIf>` components allows for providing the conditions for
 * which their respective `children` will be and rendered. Just as is the case
 * in traditional conditionals, the `<Render.If>` condition takes precedence
 * over the `<Render.ElseIf>` condition.
 */
Render.ElseIf = ({condition, children, strict = false}: RenderProps): ReactElement | undefined => {
  const isValid = isTrue(condition, strict);
  return isValid ? cast(children) : undefined;
};

/**
 * Provides a convenience for rendering children based on the inverse of the
 * given condition, this allowing for rendering components when the condition
 * is `false` rather than having to use another `<Render>` instance.
 */
Render.Else = ({children}: {children: ReactNode}): any => children;

/**
 * Provides an internal utility which determines if the given component is an
 * `<Render.Else>` Component.
 */
const isOfType = (components: any[], type: Conditional) => {
  return components.filter((component: ReactElement) => {
    if (type === Conditional.If) {
      return (
        component.type === Render.If &&
        component.type !== Render.Else &&
        component.type !== Render.ElseIf
      );
    } else if (type === Conditional.ElseIf) {
      return component.type === Render.ElseIf;
    }
    return component.type === Render.Else;
  });
};

/**
 * Provides an internal utility which returns a list of children to render when
 * the condition is true, and a  list of children to render when the condition is
 * false, if provided via `<Render.Else>...</Render.Else>`
 * statements.
 */
const getComponentTypes = (children: ReactNode) => {
  const components = React.Children.toArray(children);
  return {
    [Conditional.If]     : isOfType(components, Conditional.If),
    [Conditional.ElseIf] : isOfType(components, Conditional.ElseIf),
    [Conditional.Else]   : isOfType(components, Conditional.Else)
  };
};

/**
 * Provides an internal utility which simply casts the given value `ReactElement`.
 */
const cast = type => (type as ReactElement);

/**
 * Provides a simple utility which determines if the given condition is `true`,
 * using strict equality checks or truthy assertions. When `strict` is `true`,
 * the condition will only be evaluated as `true` if it's value is explicitly
 * set to `true`. Otherwise, if `strict` is `false` (default), the condition
 * will be evaluated using truthy assertions.
 */
const isTrue = (condition: any, strict = false) => {
  const value = isFunction(condition) ? (condition as Function)() : condition;
  return strict ? value === true : Boolean(value);
};

export default Render;
